/**
 * Admin · Cinema 4K (Mux) integration diagnostic.
 *
 * Hits GET /api/admin/cinema/health, which probes the Mux API in real
 * time and reports a green / yellow / red verdict. This is the page
 * to open whenever a seller reports "Cinema 4K is unavailable" — within
 * a few seconds it tells you whether the cause is missing env vars,
 * bad credentials, rate-limiting, or an actual Mux outage.
 */
import React, { useEffect, useState, useCallback } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowsClockwiseIcon, FilmReelIcon } from "@phosphor-icons/react";

const VERDICT_STYLES = {
  green: {
    chip: "bg-emerald-500/15 text-emerald-700 border-emerald-300",
    dot: "bg-emerald-500",
    label: "HEALTHY",
  },
  yellow: {
    chip: "bg-amber-500/15 text-amber-800 border-amber-300",
    dot: "bg-amber-500",
    label: "DEGRADED",
  },
  red: {
    chip: "bg-rose-500/15 text-rose-700 border-rose-300",
    dot: "bg-rose-500",
    label: "BROKEN",
  },
};

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between py-2 border-b border-zinc-200 last:border-0">
      <span className="text-xs uppercase tracking-wider font-bold text-zinc-500">
        {label}
      </span>
      <span className="text-sm font-mono text-zinc-900 break-all text-right ml-3">
        {String(value ?? "—")}
      </span>
    </div>
  );
}

export default function AdminCinemaHealth() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const refresh = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.get("/admin/cinema/health");
      setData(r.data);
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const verdictKey = data?.verdict || "red";
  const style = VERDICT_STYLES[verdictKey] || VERDICT_STYLES.red;

  return (
    <>
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-10" data-testid="admin-cinema-health">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-amber-400/15 border border-amber-400/30 grid place-items-center">
            <FilmReelIcon size={22} weight="fill" className="text-amber-500" />
          </div>
          <div>
            <h1 className="font-display font-black text-2xl tracking-tight">
              Cinema 4K · health
            </h1>
            <p className="text-sm text-zinc-500">
              Live diagnostic of the Mux integration powering Cinema mode.
            </p>
          </div>
          <div className="ml-auto">
            <Button
              variant="outline"
              onClick={refresh}
              disabled={busy}
              data-testid="cinema-health-refresh"
              className="gap-2"
            >
              <ArrowsClockwiseIcon size={16} className={busy ? "animate-spin" : ""} />
              {busy ? "Probing…" : "Re-check"}
            </Button>
          </div>
        </div>

        {err && (
          <div className="mt-6 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800" data-testid="cinema-health-error">
            {err}
          </div>
        )}

        {data && (
          <>
            <div className={`mt-6 rounded-2xl border ${style.chip} px-5 py-4 flex items-start gap-3`} data-testid="cinema-health-verdict">
              <span className={`mt-1.5 h-3 w-3 rounded-full ${style.dot} ${verdictKey === "green" ? "" : "animate-pulse"}`} />
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold opacity-70">
                  {style.label}
                </p>
                <p className="font-bold text-base leading-snug">
                  {data.verdict_message}
                </p>
              </div>
            </div>

            <section className="mt-8 rounded-2xl border border-zinc-200 bg-white p-5">
              <h2 className="font-bold text-sm uppercase tracking-wider text-zinc-500 mb-3">
                Environment
              </h2>
              <Row label="MUX_TOKEN_ID present" value={data.env?.MUX_TOKEN_ID_present ? "yes" : "MISSING"} />
              <Row label="MUX_TOKEN_SECRET present" value={data.env?.MUX_TOKEN_SECRET_present ? "yes" : "MISSING"} />
              <Row label="Max resolution" value={data.env?.MUX_MAX_RESOLUTION} />
              <Row label="Latency mode" value={data.env?.MUX_LATENCY_MODE} />
            </section>

            <section className="mt-5 rounded-2xl border border-zinc-200 bg-white p-5">
              <h2 className="font-bold text-sm uppercase tracking-wider text-zinc-500 mb-3">
                Mux API round-trip
              </h2>
              {data.mux_api ? (
                <>
                  <Row label="HTTP status" value={data.mux_api.status_code} />
                  <Row label="Round-trip latency" value={`${data.mux_api.elapsed_ms} ms`} />
                  {data.mux_api.body_preview && (
                    <div className="mt-3">
                      <p className="text-xs uppercase tracking-wider font-bold text-zinc-500 mb-1">
                        Response body (first 300 chars)
                      </p>
                      <pre className="text-xs bg-zinc-50 border border-zinc-200 rounded-md p-2 overflow-x-auto whitespace-pre-wrap break-all">
                        {data.mux_api.body_preview}
                      </pre>
                    </div>
                  )}
                  {data.mux_api.error && (
                    <div className="mt-3">
                      <p className="text-xs uppercase tracking-wider font-bold text-rose-600 mb-1">
                        Transport error
                      </p>
                      <pre className="text-xs bg-rose-50 border border-rose-200 rounded-md p-2 overflow-x-auto whitespace-pre-wrap break-all">
                        {data.mux_api.error}
                      </pre>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-zinc-500">Not probed (env vars missing).</p>
              )}
            </section>

            <section className="mt-5 rounded-2xl border border-zinc-200 bg-white p-5">
              <h2 className="font-bold text-sm uppercase tracking-wider text-zinc-500 mb-3">
                Last stored Mux stream
              </h2>
              {data.recent_stream ? (
                <>
                  <Row label="Bidorah stream id" value={data.recent_stream.stream_id} />
                  <Row label="Mux live stream id" value={data.recent_stream.mux_live_stream_id} />
                  <Row label="Playback id" value={data.recent_stream.playback_id} />
                  <Row label="Status" value={data.recent_stream.status} />
                  <Row label="Created at" value={data.recent_stream.created_at} />
                </>
              ) : (
                <p className="text-sm text-zinc-500">
                  No Mux streams have been created on this environment yet.
                </p>
              )}
            </section>

            <p className="mt-6 text-xs text-zinc-500">
              Tip: if this page reads <span className="font-bold text-rose-600">BROKEN</span>{" "}
              with "MUX_TOKEN_ID / MUX_TOKEN_SECRET not set", open your
              Emergent deployment dashboard, add both env vars from your
              mux.com account, and redeploy.
            </p>
          </>
        )}
      </div>
      <Footer />
    </>
  );
}
